import { isValidObject, isValidDate } from './validationHelper'
import type { StateItem, StateItemValue } from '@/types/devices'

export const convertUTCToTSITimezoneDate = (str: string): string => {
  //Ex: 2022-02-16T00:00:00.000Z -> 02/16/2022
  const d = new Date(str)
  if (!isNaN(d.getTime())) {
    // date object is valid
    return d.toLocaleString('en-US', {
      timeZone: 'America/Chicago',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    })
  }

  return ''
}

export const convertRFC3339ToMonthDayYearDateString = (str: string): string => {
  //Ex: 2023-02-15 or 2023-02-15T00:00:00 -> 02/15/2023
  try {
    const cstDateParts = str.split('-')
    cstDateParts[2] = cstDateParts[2].split('T')[0]
    return `${cstDateParts[1]}/${cstDateParts[2]}/${cstDateParts[0]}`
  } catch {
    // return empty string
  }

  return ''
}

export const convertObjectToArray = <T>(
  value: T[] | Record<string, T>,
  convertFn?: (key: string) => T
): T[] => {
  if (Array.isArray(value)) {
    return value
  }

  if (value && typeof value === 'object') {
    const array: T[] = []
    const keys = Object.keys(value)
    for (const key of keys) {
      if (value[key]) {
        if (typeof convertFn === 'function') {
          array.push(convertFn(key))
        } else {
          array.push(value[key])
        }
      }
    }

    return array
  }

  return []
}

export const getLatestTimestamp = (timestamps: string[]): string | null => {
  let latestTimestamp = 0

  if (timestamps?.length) {
    for (const timestamp of timestamps) {
      const formattedTimestamp = timestamp ? new Date(timestamp).getTime() : 0
      if (formattedTimestamp > latestTimestamp) {
        latestTimestamp = formattedTimestamp
      }
    }
    return latestTimestamp ? new Date(latestTimestamp).toISOString() : null
  }

  return null
}

export const getLatestObjectByTimestamp = <T extends object>(
  arrayOfObjects: T[],
  key: string
): T | null => {
  let latestObject: T | null = null
  let latestTimestamp = 0

  if (arrayOfObjects?.length && key) {
    for (const item of arrayOfObjects) {
      const newDate = isValidObject(item)
        ? new Date((item as Record<string, unknown>)[key] as string)
        : null
      if (isValidDate(newDate) && newDate.getTime() > latestTimestamp) {
        latestTimestamp = newDate.getTime()
        latestObject = item
      }
    }
  }

  return latestObject
}

export const getObjectProperty = <T extends object>(
  objectValue: unknown,
  key: string
): (T extends Record<string, unknown> ? T[typeof key] : unknown) | null => {
  return isValidObject(objectValue) && objectValue.hasOwnProperty(key)
    ? ((objectValue as Record<string, unknown>)[key] as T extends Record<string, unknown>
        ? T[typeof key]
        : unknown)
    : null
}

export const getObjectByKeyValue = <T extends object>(
  arrayOfObjects: T[],
  key: string,
  value: unknown
): T | null => {
  if (arrayOfObjects?.length) {
    for (const item of arrayOfObjects) {
      if (getObjectProperty(item, key) === value) {
        return item
      }
    }
  }

  return null
}

export const getObjectValueByPath = (object: Record<string, unknown>, path: string): unknown => {
  if (isValidObject(object) && typeof path === 'string') {
    let value: unknown = object
    const splittedPath = path.split('.')
    if (splittedPath.length) {
      for (const keyPath of splittedPath) {
        value = getObjectProperty(value as Record<string, unknown>, keyPath)
      }
      return value
    }
  }

  return null
}

// This function flattens a device state item object into a simple key-value object
// where each key is the Key from the item and the value is the Value from the item
export const flattenStateItemObject = (
  stateItem: StateItem
): Record<string, StateItemValue> | null => {
  if (!isValidObject(stateItem) || !stateItem.value || !Array.isArray(stateItem.value)) {
    return null
  }
  return stateItem.value.reduce<Record<string, StateItemValue>>((acc, item) => {
    acc[item.Key] = parseStateItemValue(item.Value) as StateItemValue
    return acc
  }, {})
}

const parseStateItemValue = (stateItemValue: StateItemValue): unknown => {
  if (Array.isArray(stateItemValue)) {
    return stateItemValue.find((v) => v.Key === 'value')?.Value ?? null
  } else if (typeof stateItemValue === 'boolean') {
    return stateItemValue
  }
  return null
}
